// ///////////////////////////////////////////////////////
// Composant pour la gestion de la page les exterieurs
// ///////////////////////////////////////////////////////
// Creation : 25/07/2022    -> CGR
// ///////////////////////////////////////////////////////

// ///////////////////////////////////
// Imports

// Imports system
import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom"
import useKrpano from "react-krpano-hooks";
import SvgIcon from "../../components/svg/svg";

// Import context
import {
  languageContext,
  programmeContext,
  programmeNameContext,
} from "../../contexts";


// ///////////////////////////////////
// Component

const Pano = ({
  view,
  variante,
  scene,
  count,
  setDirection,
  setInitialDir,
}) => {
  // Partie krPano

  // /////////////////////////////
  // Properties
  const navigate = useNavigate()
  const { search } = useLocation()
  const lang = useContext(languageContext)
  const programmeName = useContext(programmeNameContext)
  const programmeDatas = useContext(programmeContext)
  const extDatas = programmeDatas.pagesDetails.exterieurs
  const [hotspots, setHotspots] = useState([])
  const [positionMemory, setPositionMemory] = useState(null)
  const [forceUpdate, setForceUpdate] = useState(0)
  const updateType = useRef("view")
  const [canUseGyro, setCanUseGyro] = useState(false)
  const [gyro, setGyro] = useState(false)
  const [cardBoard, setCardBoard] = useState(false)
  const { containerRef, callKrpano, krpanoState } = useKrpano({
    scriptPath: `/public/sources/krPanoBuilder/viewer/krpano.js`,
    embeddingParams: {
      swf: `/public/programmes/${programmeName}/exterieurs/tour.swf`,
      xml: `/public/programmes/${programmeName}/exterieurs/tour.xml`,
      target: "react-krpano",
      html: "prefer",
    },
  })

  // /////////////////////////////
  // Functions

  const setKrDatas = () => {

    const kr = containerRef.current.children[0];
    
    if (kr) {
        kr.set("view.fov", scene.fovRanges[1])
        kr.set("view.fovmin", scene.fovRanges[0])
        kr.set("view.fovmax", scene.fovRanges[2])
    } else {
      const kr = containerRef.current.children[0];
      setTimeout(() => {
        kr.set("view.fov", scene.fovRanges[1])
        kr.set("view.fovmin", scene.fovRanges[0])
        kr.set("view.fovmax", scene.fovRanges[2])
      }, 400)
    }

    callKrpano(`set(view.fov, ${scene.fovRanges[1]});`);
    callKrpano(`set(view.fovmin, ${scene.fovRanges[0]});`);
    callKrpano(`set(view.fovmax, ${scene.fovRanges[2]});`);

    callKrpano(`set(view.hlookat, ${scene.start[0]});`);
    callKrpano(`set(view.vlookat, ${scene.start[1]});`);
    callKrpano(`set(view.limitview, range);`);
    callKrpano(`set(view.hlookatmin, ${scene.ranges.xMin});`);
    callKrpano(`set(view.hlookatmax, ${scene.ranges.xMax});`);
    callKrpano(`set(view.vlookatmin, ${scene.ranges.yMin});`);
    callKrpano(`set(view.vlookatmax, ${scene.ranges.yMax});`);

    setPois();

    // Gestion des langues des elements vr
    if (programmeDatas.useVRforKr) {
      callKrpano(`set(hotspot[vr_povbtn].html, ${lang === "en" ? "points of view" : "points de vue"});`);
      callKrpano(`set(hotspot[vr_varbtn].html, ${lang === "en" ? "ambiences" : "ambiances"});`);

      for (const v of extDatas.variantes) {
        callKrpano(`set(hotspot[webvr_var-${v.id}].html, ${v.name[lang]});`);
        callKrpano(`set(hotspot[webvr_var-${v.id}].onclick, clicOnVAR(${v.id}, scene_ext$-${v.id}, ${v.name[lang]}));`);
        callKrpano(`set(hotspot[webvr_var-${v.id}-ico-normal].onclick, clicOnVAR(${v.id}, scene_ext$-${v.id}, ${v.name[lang]}));`);

      }

      for (const v of extDatas.views) {
        callKrpano(`set(hotspot[webvr_pov-${v.id}].html, ${v.title[lang]});`);
        callKrpano(`set(hotspot[webvr_pov-${v.id}].onclick, clicOnPOV(${v.id}, scene_ext${v.id}-$, ${v.title[lang]}));`);
        callKrpano(`set(hotspot[webvr_pov-${v.id}-ico-normal].onclick, clicOnPOV(${v.id}, scene_ext${v.id}-$, ${v.title[lang]}));`);
      }
    }


  };

  const setPois = () => {
    for (let spot of hotspots) {
      callKrpano(`removehotspot(${spot});`);
    }
    // On vide la memoire
    setHotspots([]);
    setTimeout(() => {
      // Gestion des poi
      for (const poi of scene.pois) {
        callKrpano(`addhotspot(poi_${poi.id}_ctn);`);
        callKrpano(`hotspot[poi_${poi.id}_ctn].loadstyle(skin-direction);`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].ath, ${poi.position[0]});`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].atv, ${poi.position[1]});`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].zoom,false);`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].distorted, false);`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].scale, 0.8);`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].width, 60);`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].height, 60);`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].x, 0);`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].y, 8);`);

        callKrpano(`set(hotspot[poi_${poi.id}_ctn].onclick, js(window.somVar(${poi.link[0]})));`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].onhover, tween(scale, 1.2, 0.15); tween(hotspot[poi_${poi.id}_svg].scale, 0.7, 0.15); tween(hotspot[poi_${poi.id}_svg].y, -35, 0.15); tween(hotspot[poi_${poi.id}_txt].y, 62, 0.15);)`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].onout, tween(scale, 0.8, 0.15); tween(hotspot[poi_${poi.id}_svg].scale, 0.4, 0.15); tween(hotspot[poi_${poi.id}_svg].y, -13, 0.15); tween(hotspot[poi_${poi.id}_txt].y, 50, 0.15);)`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].bgcolor, 0x${poi.color.slice(1)});`);
        callKrpano(`set(hotspot[poi_${poi.id}_ctn].bgroundedge, 30;`);

        callKrpano(`addhotspot(poi_${poi.id}_txt);`);
        callKrpano(`hotspot[poi_${poi.id}_txt].loadstyle(skin-direction-text);`);
        callKrpano(`set(hotspot[poi_${poi.id}_txt].ath, ${poi.position[0]});`);
        callKrpano(`set(hotspot[poi_${poi.id}_txt].atv, ${poi.position[1]});`);
        callKrpano(`set(hotspot[poi_${poi.id}_txt].height, 120);`);
        callKrpano(`set(hotspot[poi_${poi.id}_txt].textalign, centerbottom);`);
        callKrpano(`set(hotspot[poi_${poi.id}_txt].bg, false);`);
        callKrpano(`set(hotspot[poi_${poi.id}_txt].zoom,false);`);
        callKrpano(`set(hotspot[poi_${poi.id}_txt].html, ${poi.text[lang]});`);
        callKrpano(`set(hotspot[poi_${poi.id}_txt].distorted, false);`);
        callKrpano( `set(hotspot[poi_${poi.id}_txt].onclick, js(window.somVar(${poi.link[0]})));`);
        callKrpano(`set(hotspot[poi_${poi.id}_txt].css, "overflow : visible; color: black; font-size: 20px; font-family: BarlowCondensed-SemiBold; width:auto; border-radius:4px; pointer-events:none!important; ";`);

        callKrpano(`addhotspot(poi_${poi.id}_svg);`);
        callKrpano(`set(hotspot[poi_${poi.id}_svg].ath, ${poi.position[0]});`);
        callKrpano(`set(hotspot[poi_${poi.id}_svg].atv, ${poi.position[1]});`);
        callKrpano(`set(hotspot[poi_${poi.id}_svg].x, 80;`);
        callKrpano(`set(hotspot[poi_${poi.id}_svg].y, -13;`);
        callKrpano(`set(hotspot[poi_${poi.id}_svg].distorted,false);`);
        callKrpano(`set(hotspot[poi_${poi.id}_svg].zorder, 10);`);
        callKrpano(`set(hotspot[poi_${poi.id}_svg].scale, 0.4);`);
        callKrpano(`set(hotspot[poi_${poi.id}_svg].edge, center);`);
        callKrpano(`set(hotspot[poi_${poi.id}_svg].url, /public/icons/VIM/${poi.icon}.svg);`);
        callKrpano(`set(hotspot[poi_${poi.id}_svg].onhover, tween(hotspot[poi_${poi.id}_ctn].scale, 1.2, 0.15); tween(scale, 0.7, 0.15); tween(y, -35, 0.15); tween(hotspot[poi_${poi.id}_txt].height, 140, 0.15);)`);
        callKrpano(`set(hotspot[poi_${poi.id}_svg].onout, tween(hotspot[poi_${poi.id}_ctn].scale, 0.8, 0.15); tween(scale, 0.4, 0.15); tween(y, -13, 0.15); tween(hotspot[poi_${poi.id}_txt].height, 120, 0.15);)`);
        callKrpano(`set(hotspot[poi_${poi.id}_svg].onclick, js(window.somVar(${poi.link[0]})));`);

        // Ajout du hotspot en memoire
        setHotspots((old) => [...old, `poi_${poi.id}`]);
      }
    }, 2);

    const kr = containerRef.current.children[0];
    if (kr && programmeDatas.useVRforKr) {

      extDatas.variantes.map(v => {
        kr.call(`hotspot[webvr_var-${v.id}].loadstyle(webvr_button_style)`)
        const a = kr.get(`hotspot[webvr_var-${v.id}-ico-normal].url`)
        if (a) {
          kr.set(`hotspot[webvr_var-${v.id}-ico-normal].url`, a.replace("white/", ""))
        }
        
        
      })

      extDatas.views.map(v => {
        // std
        kr.call(`hotspot[webvr_pov-${v.views[0].id}].loadstyle(webvr_button_style)`)
        const p = kr.get(`hotspot[webvr_pov-${v.views[0].id}-ico-normal].url`)
        if (p) {
          kr.set(`hotspot[webvr_pov-${v.views[0].id}-ico-normal].url`, p.replace("white/", ""))
        }
        // mmp
        kr.set(`hotspot[webvr_minimap_e${v.views[0].id}].url`, '/public/images/eye.png')
      })

      kr.call(`hotspot[webvr_pov-${scene.id}].loadstyle(revert-webvr_button_style)`)
      kr.set(`hotspot[webvr_minimap_e${scene.id}].url`, '/public/images/eye-white.png')
      kr.call(`hotspot[webvr_var-${variante.id}].loadstyle(revert-webvr_button_style)`)

      const b = kr.get(`hotspot[webvr_var-${variante.id}-ico-normal].url`)
      if (b) {
        kr.set(`hotspot[webvr_var-${variante.id}-ico-normal].url`, b.replace("vr/", "vr/white/"))
      }

      const q = kr.get(`hotspot[webvr_pov-${scene.id}-ico-normal].url`)
      if (q) {
        kr.set(`hotspot[webvr_pov-${scene.id}-ico-normal].url`, q.replace("vr/", "vr/white/"))
      }

    }
  };

  // /////////////////////////////
  // Effects
  useEffect(() => {

    // variable global pour effet krPano
    window.endVr = () => {
        setCardBoard(false)
        setGyro(false)
    }

    setCanUseGyro(navigator.platform !== "Win32")
  
}, [])

useEffect(() => {
  window.varFromKr = (where) => {
    navigate(
      `/${programmeDatas.urlName}/exterieurs/${view.title[lang]}/0/${where}${search}`
    )
  }

  window.viewFromKr = (where) => {
    navigate(
      `/${programmeDatas.urlName}/exterieurs/${where}/0/${variante.name[lang]}${search}`
    )
  }
}, [view, variante])

  useEffect(() => {
    if (containerRef.current !== undefined) {
      if (containerRef.current.children.length === 2) {
        containerRef.current.removeChild(containerRef.current.children[0]);
      }
    }
  }, [containerRef, view]);

  useEffect(() => {
    if (variante) {
      // au changement de décoration / prestation
      const kr = containerRef.current.children[0];
      if (kr) {
        for (let spot of hotspots) {
          callKrpano(`removehotspot(${spot});`);
        }

        const posMem = [
          kr.get("view.hlookat"),
          kr.get("view.vlookat"),
          kr.get("view.camroll"),
        ];
        updateType.current = "variante";
        setForceUpdate((l) => l + 1);
        setPositionMemory(posMem);
        setPois();
      }
    }
  }, [variante]);

  useEffect(() => {
    if (krpanoState.isEmbedded && view) {
      for (let spot of hotspots) {
        callKrpano(`removehotspot(${spot});`);
      }
      // On vide la memoire
      setHotspots([]);

      callKrpano(
        `loadscene('${"scene_"}ext${scene.id}-${variante.id}', null, MERGE, BLEND(0.4));`
      );
      if (
        krpanoState.isEmbedded &&
        programmeDatas &&
        view.title[lang] &&
        view.title[lang] !== ""
      ) {
        setKrDatas();
      }
    }
  }, [krpanoState.isEmbedded, scene, variante]);

  useEffect(() => {
    // if (updateType.current === "room") {
    const update = () =>
      setTimeout(() => {
        const kr = containerRef.current.children[0];
        if (kr) {
          setKrDatas();
          const hlookat = kr.get("view.hlookat");
          const camroll = kr.get("view.camroll");
          setInitialDir(camroll + hlookat);
        } else {
          update();
        }
      }, 50);

    update();
    // }
  }, [view]);

  useEffect(() => {
    // Gestion de la direction de la puce minimap en live

    let int;
    if (krpanoState.isEmbedded) {
      int = setInterval(() => {
        const kr = containerRef.current.children[0];
        if (kr) {
          const hlookat = kr.get("view.hlookat");
          const camroll = kr.get("view.camroll");
          setDirection(camroll + hlookat);
        }
      }, 100);
    }
    return () => clearInterval(int);
  }, [krpanoState.isEmbedded]);

  useEffect(() => {
    if (positionMemory) {
      const kr = containerRef.current.children[0];

      kr.set("view.hlookat", positionMemory[0]);
      kr.set("view.vlookat", positionMemory[1]);
      kr.set("view.camroll", positionMemory[2]);
    }
  }, [positionMemory]);

  useEffect(() => {
    const kr = containerRef.current.children[0]

    if (kr) {
        if (cardBoard) { kr.call("webvr.enterVR();") } 
        else  { 
            kr.call("webvr.exitVR();") 
        }
    }
}, [cardBoard])

useEffect(() => {
    const kr = containerRef.current.children[0]

    if (kr) {
        if (gyro) { kr.set("plugin[skin_gyro].enabled", 'true') } 
        else  { kr.set("plugin[skin_gyro].enabled", 'false') }
    }
}, [gyro])

  // /////////////////////////////
  // Render
  return (
    <div id="pano">
      <div
        ref={containerRef}
        style={{ position: "absolute", width: "100%", height: "100%" }}
      />

      {(programmeDatas.useVRforKr && canUseGyro) &&
        <>
          {navigator?.userAgent?.toLowerCase()?.includes("oculus") ? 
            /* Cas 1. occulus */
            <div id = "gyrocasque" style = {{display : "flex", flexDirection : "column"}}>
              {!cardBoard &&
                <button id = "vrButton" className = {`no-hover ${cardBoard ? "mainBackground" : "inactive"}`} onClick = {() => setCardBoard(true)}>Lancer la VR</button>
              }
            </div>
              :
            /* Cas 2. mobile */
            <div id = "gyro">
                <button className = {`no-hover ${gyro ? "mainBackground" : "inactive"}`} onClick = {() => setGyro(g => !g)}>
                    <SvgIcon icon = "gyroscope" />
                </button>     
            </div>
          }
        </>
      }


    {/* <div id = "gyrocasque" style = {{display : "flex", flexDirection : "column"}}>
        {!cardBoard &&
          <button id = "vrButton" className = {`no-hover ${cardBoard ? "mainBackground" : "inactive"}`} onClick = {() => setCardBoard(true)}>Lancer la VR</button>
        }
      </div> */}

    </div>
  );
};

export default Pano;
